import { useContext } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { LanguageContext } from "~/providers/LanguageProvider";

import { useCategoryStore, useDisclosureStore, useTopicStore, useHelperStore } from "~/stores";

import {
    SEARCH_TABS,
    DetailsResponseItem,
    NormalizedCategory,
    NormalizedTopic
} from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";

import { fetchGraphicsMeta, fetchReportsMeta } from "~/services/api";
import { normalizeCategoryMetaData, normalizeTopicMetaData } from "~/utils/normalize.utils";
import useSolrTopicsCounter from "./useSolrTopicsCounter";
import { TabKeys } from "~/stores/tab/tab.state";

const useSolrMetaData = () => {
    const { language } = useContext(LanguageContext);
    const { fetchSolrTopicsDocsCount } = useSolrTopicsCounter();

    // Store states
    const isModalOpen = useHelperStore(state => state.isModalOpen);
    const disclosures = useDisclosureStore(state => state.disclosures);

    // Store dispatch methods
    const dispatchCategories = useCategoryStore(state => state.dispatch);
    const dispatchTopics = useTopicStore(state => state.dispatch);
    const dispatchHelper = useHelperStore(state => state.dispatch);

    // Store methods
    const getDisclosureIDs = useDisclosureStore(state => state.getDisclosureIDs);
    const resetCategories = useCategoryStore(state => state.reset);
    const resetTopics = useTopicStore(state => state.reset);

    /** Fetches and sets the normalized Categories and Topics */
    const setSearchTopicsCount = async (data: DetailsResponseItem[], tab: TabKeys) => {
        // Normalize the Categories metadata
        const metaCategories: NormalizedCategory[] = normalizeCategoryMetaData(data);

        // Normalize the Topics metadata
        const metaTopics: NormalizedTopic[] = normalizeTopicMetaData(data);

        unstable_batchedUpdates(() => {
            // Set the metadata masterlists
            dispatchCategories({ type: SearchActions.SET_CATEGORY_ITEMS, payload: metaCategories });
            dispatchTopics({ type: SearchActions.SET_TOPIC_ITEMS, payload: metaTopics });

            // Set the default Category
            const category = metaCategories.find(item => item.disclosureId === disclosures[0].exploreId);
            dispatchCategories({ type: SearchActions.SET_ACTIVE_CATEGORY, payload: (category ?? null) });
        });

        // Fetch all topics count metadata
        return await fetchSolrTopicsDocsCount(metaTopics, tab);
    }

    /** Fetches the full Disclosures/Categories/Topics metadata by TabKey (graphics or reports) */
    const fetchMetadata = async (tab: TabKeys) => {
        let response = null;

        unstable_batchedUpdates(() => {
            resetCategories();
            resetTopics();
            dispatchHelper({ type: SearchActions.SET_IS_LOADING, payload: true });
        });

        try {
            if (tab === SEARCH_TABS.IMAGES) {
                response = await fetchGraphicsMeta({
                    DisclosureType: getDisclosureIDs(),
                    SearchText: ""
                }, language);
            }

            if (tab === SEARCH_TABS.REPORTS) {
                response = await fetchReportsMeta({
                    DisclosureType: getDisclosureIDs(),
                    SearchText: ""
                }, language);
            }


            return await setSearchTopicsCount(response?.data ?? [], tab);
        } catch (err: any) {
            console.error(err.message)
        } finally {
            dispatchHelper({ type: SearchActions.SET_IS_LOADING, payload: false });
        }
    }

    return {
        isModalOpen,
        fetchMetadata,
    }
}

export default useSolrMetaData;
